// @ts-nocheck
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { penly } from 'api';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

import { getAllMeetings } from 'actions/access';
import { findMeetingsByEmail } from 'components/access/services/meetingsLookup';
import MeetingsCalendar from './MeetingsCalendar';

const getMeetingDateText = (meeting) => {
    const startTime = meeting?.booking?.startTime || meeting?.booking?.calendlyEvent?.start_time;
    if (!startTime) {
        return 'Tidspunkt ikke angivet';
    }

    return new Date(startTime).toLocaleString('da-DK', {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
};

const getMeetingTitleText = (meeting) => {
    const orderNumber = meeting?.order?.orderNumber ? `#${meeting.order.orderNumber}` : null;
    const customerName = `${meeting?.order?.customer?.firstName || ''} ${meeting?.order?.customer?.lastName || ''}`.trim();
    const advisorName = meeting?.booking?.vendorsAttendees?.join(', ');

    return [orderNumber, customerName || 'Ukendt kunde', advisorName].filter(Boolean).join(' · ');
};

const Meetings = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { organisationId } = useParams();

    const [meetings, setMeetings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [findModalOpen, setFindModalOpen] = useState(false);
    const [emailQuery, setEmailQuery] = useState('');
    const [emailSearchTouched, setEmailSearchTouched] = useState(false);
    const [matchedMeetings, setMatchedMeetings] = useState([]);
    const [loadingMatchedMeetings, setLoadingMatchedMeetings] = useState(false);
    const latestMeetingSearchIdRef = useRef(0);
    const lastCompletedMeetingSearchEmailRef = useRef('');

    useEffect(() => {
        const fetch = async () => {
            const meetings = await dispatch(getAllMeetings(organisationId));
            setMeetings(meetings);
            setLoading(false);
        };
        fetch();
    }, [dispatch, organisationId]);

    const normalizedEmailQuery = emailQuery.trim().toLowerCase();
    const emailLooksValid = /^\S+@\S+\.\S+$/.test(normalizedEmailQuery);

    useEffect(() => {
        if (!findModalOpen) {
            return;
        }

        latestMeetingSearchIdRef.current += 1;
        const activeSearchId = latestMeetingSearchIdRef.current;

        if (!normalizedEmailQuery) {
            setMatchedMeetings([]);
            setLoadingMatchedMeetings(false);
            return;
        }

        if (!emailLooksValid) {
            setMatchedMeetings([]);
            setLoadingMatchedMeetings(false);
            return;
        }

        if (lastCompletedMeetingSearchEmailRef.current === normalizedEmailQuery) {
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                setLoadingMatchedMeetings(true);
                const nextMatches = await findMeetingsByEmail({
                    organisationId,
                    email: normalizedEmailQuery,
                    getUserByEmail: penly.getUserByEmail,
                    getAllMeetings: async (targetOrganisationId) => dispatch(getAllMeetings(targetOrganisationId)),
                });

                if (activeSearchId !== latestMeetingSearchIdRef.current) {
                    return;
                }

                setMatchedMeetings(nextMatches);
                lastCompletedMeetingSearchEmailRef.current = normalizedEmailQuery;
            } finally {
                if (activeSearchId === latestMeetingSearchIdRef.current) {
                    setLoadingMatchedMeetings(false);
                }
            }
        }, 300);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [dispatch, emailLooksValid, findModalOpen, normalizedEmailQuery, organisationId]);

    const closeFindModal = () => {
        setFindModalOpen(false);
        setEmailQuery('');
        setEmailSearchTouched(false);
        setMatchedMeetings([]);
        setLoadingMatchedMeetings(false);
        lastCompletedMeetingSearchEmailRef.current = '';
    };

    const openMeetingOrder = (orderId) => {
        if (!orderId) {
            return;
        }
        closeFindModal();
        navigate(`/${organisationId}/access/orders/${orderId}`);
    };

    return (
        <>
            { loading && (
                <div className="App-section-loading">
                    <CircularProgress color="primary" size={ 40 } />
                </div>
            ) }

            { !loading && (
                <>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                        <Button
                            variant="outlined"
                            onClick={ () => {
                                setFindModalOpen(true);
                                setEmailSearchTouched(true);
                                setMatchedMeetings([]);
                                lastCompletedMeetingSearchEmailRef.current = '';
                            } }
                        >
                            Find bookede møder
                        </Button>
                    </Box>

                    <MeetingsCalendar meetings={ meetings } />
                </>
            ) }

            <Dialog
                open={ findModalOpen }
                onClose={ closeFindModal }
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Find bookede møder</DialogTitle>

                <DialogContent>
                    <Stack spacing={ 2 } sx={{ pt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Søg efter bookede møder med kundens e-mailadresse.
                        </Typography>

                        <TextField
                            value={ emailQuery }
                            onChange={ (event) => {
                                setEmailQuery(event.target.value);
                                setEmailSearchTouched(true);
                            } }
                            placeholder="kunde@eksempel.dk"
                            label="E-mail"
                            size="small"
                            fullWidth
                        />

                        { emailSearchTouched && !normalizedEmailQuery && (
                            <Alert severity="warning">Indtast en e-mailadresse for at søge efter bookede møder.</Alert>
                        ) }

                        { emailSearchTouched && normalizedEmailQuery && !emailLooksValid && (
                            <Alert severity="warning">Indtast en gyldig e-mailadresse.</Alert>
                        ) }

                        { loadingMatchedMeetings && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <CircularProgress size={ 24 } />
                            </Box>
                        ) }

                        { emailSearchTouched && emailLooksValid && !loadingMatchedMeetings && matchedMeetings.length === 0 && (
                            <Alert severity="info">Ingen bookede møder fundet for den e-mail.</Alert>
                        ) }

                        { emailSearchTouched && !loadingMatchedMeetings && matchedMeetings.length > 0 && (
                            <List dense>
                                { matchedMeetings.map((meeting, index) => (
                                    <ListItem
                                        key={ meeting?.booking?.eventId || meeting?.order?.orderId || index }
                                        divider
                                        secondaryAction={ (
                                            <Button
                                                size="small"
                                                onClick={ () => openMeetingOrder(meeting?.order?.orderId) }
                                                disabled={ !meeting?.order?.orderId }
                                            >
                                                Tilknyt denne ordre
                                            </Button>
                                        ) }
                                    >
                                        <ListItemText
                                            primary={ getMeetingTitleText(meeting) }
                                            secondary={ getMeetingDateText(meeting) }
                                        />
                                    </ListItem>
                                )) }
                            </List>
                        ) }
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={ closeFindModal }>Luk</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Meetings;
